import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Password } from 'primereact/password';
import { dialogInputStore, DialogType } from '../dialogInputStore';
import { ProgressSpinner } from 'primereact/progressspinner';

const DialogInput = observer(() => {
    const dialogStore = dialogInputStore;

    const handleConfirm = () => {
        if (dialogStore) {
            dialogInputStore.confirm();
        }
    };

    const handleCancel = () => {
        if (dialogStore) {
            dialogStore.cancel();
        }
    };

    if (!dialogStore || !dialogStore?.dialog?.open) {
        return null;
    }

    const { dialog } = dialogStore;
    const isEditDialog: boolean =
        dialog.type === DialogType.MULTI_FIELD ||
        dialog.type === DialogType.TEXT ||
        dialog.type === DialogType.NUMBER;
    const isDeleteDialog: boolean = dialog.type === DialogType.DELETE;
    const isWaitDialog: boolean = dialog.type === DialogType.WAIT;

    return (
        <Dialog
            visible={dialog.open}
            modal
            header={dialog.title}
            footer={() =>
                dialogStore.dialogHasFooter ? (
                    <div>
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            className="p-button-secondary"
                            data-testid={dialogStore.buttonCancelTestId || 'dialog-input-cancel'}
                            onClick={handleCancel}
                            disabled={dialogStore.buttonCancelDisabled}
                        />
                        <Button
                            label={dialogStore.buttonAcceptLabel || ''}
                            icon={dialogStore.buttonAcceptIcon}
                            className={dialogStore.buttonAcceptClassName || 'p-button-primary'}
                            data-testid={dialogStore.buttonAcceptTestId || 'dialog-input-confirm'}
                            onClick={handleConfirm}
                            disabled={dialogStore.buttonAcceptDisabled}
                            loading={dialogStore.loading}
                        />
                    </div>
                ) : null
            }
            data-testid={'dialog-input'}
            style={{ maxWidth: '500px', width: '90%' }}
            closable={dialogStore.dialogCloseable}
            onHide={handleCancel}
        >
            <div style={{ marginBottom: '1rem' }}>{dialog.message || ''}</div>
            {isWaitDialog ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem 0',
                    }}
                >
                    <ProgressSpinner
                        style={{ width: '50px', height: '50px' }}
                        strokeWidth="4"
                        animationDuration="1s"
                        data-testid="dialog-wait-spinner"
                    />
                </div>
            ) : null}
            {isEditDialog ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {(dialog.fields || []).map(field => (
                        <div
                            key={field.key}
                            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                        >
                            <label
                                htmlFor={field.key}
                                style={{ fontWeight: '500' }}
                            >
                                {field.label}
                                {field.required && <span style={{ color: 'red' }}>*</span>}
                            </label>
                            {field.type === 'password' ? (
                                <Password
                                    id={field.key}
                                    value={
                                        (dialogStore.dialogInputValues[field.key] as string) || ''
                                    }
                                    onChange={(e: any) => {
                                        dialogStore.updateMultiFieldValue(
                                            field.key,
                                            e.target.value
                                        );
                                    }}
                                    placeholder={field.placeholder || ''}
                                    data-testid={`dialog-input-field-${field.key}`}
                                    style={{ width: '100%' }}
                                    feedback={false}
                                    toggleMask
                                />
                            ) : field.type === 'number' ? (
                                <InputNumber
                                    id={field.key}
                                    value={dialogStore.dialogInputValues[field.key] as number}
                                    onChange={e => {
                                        dialogStore.updateMultiFieldValue(
                                            field.key,
                                            e.value ?? null
                                        );
                                    }}
                                    suffix={field.suffix}
                                    format={field.suffix ? true : false}
                                    placeholder={field.placeholder || ''}
                                    data-testid={`dialog-input-field-${field.key}`}
                                    style={{ width: '100%' }}
                                    showButtons
                                />
                            ) : field.type === 'textarea' ? (
                                <InputTextarea
                                    id={field.key}
                                    value={
                                        (dialogStore.dialogInputValues[field.key] as string) || ''
                                    }
                                    onChange={(e: any) => {
                                        dialogStore.updateMultiFieldValue(
                                            field.key,
                                            e.target.value
                                        );
                                    }}
                                    placeholder={field.placeholder || ''}
                                    data-testid={`dialog-input-field-${field.key}`}
                                    style={{ width: '100%' }}
                                    rows={field.rows || 3}
                                    autoResize
                                />
                            ) : (
                                <InputText
                                    id={field.key}
                                    value={
                                        (dialogStore.dialogInputValues[field.key] as string) || ''
                                    }
                                    onChange={(e: any) => {
                                        dialogStore.updateMultiFieldValue(
                                            field.key,
                                            e.target.value
                                        );
                                    }}
                                    placeholder={field.placeholder || ''}
                                    data-testid={`dialog-input-field-${field.key}`}
                                    style={{ width: '100%' }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            ) : null}
            {isDeleteDialog ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        padding: '1rem',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '0.5rem',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#dc2626',
                            fontWeight: '600',
                        }}
                    >
                        <i
                            className="pi pi-exclamation-triangle"
                            style={{ fontSize: '1.25rem' }}
                        ></i>
                        <span>Warnung: Diese Aktion kann nicht rückgängig gemacht werden</span>
                    </div>

                    {dialogStore.dialogInputValues['searchWord'] && (
                        <div style={{ marginTop: '0.5rem' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500',
                                    color: '#374151',
                                }}
                            >
                                Geben Sie "{dialogStore.dialogInputValues['searchWord']}" ein, um zu
                                bestätigen:
                            </label>
                            <InputText
                                value={
                                    (dialogStore.dialogInputValues['confirmationText'] as string) ||
                                    ''
                                }
                                onChange={e =>
                                    dialogStore.updateMultiFieldValue(
                                        'confirmationText',
                                        e.target.value
                                    )
                                }
                                placeholder={`Geben Sie "${dialogStore.dialogInputValues['searchWord']}" ein`}
                                data-testid={'delete-confirmation-dialog-input'}
                                style={{
                                    width: '100%',
                                    borderColor: '#fca5a5',
                                }}
                                className="p-inputtext-sm"
                            />
                        </div>
                    )}
                </div>
            ) : null}
        </Dialog>
    );
});

export default DialogInput;
